import { useEffect, useRef, useState } from 'react';
import { Badge, Box, Flex, Text, Tooltip } from '@optiaxiom/react';
import {
  IconAgent,
  IconArrowsRotate,
  IconCheckCircleSolid,
  IconCodeBranch,
  IconSparkles,
} from '@optimizely/axiom-icons';

import { TruncatedList } from '@frontend/components/ui';

import { DirectoryAgent } from '../../../services/agent-directory';

import { styles } from './AgentCardView.css';

type AgentCardViewProps = {
  agent: DirectoryAgent;
  isSelected?: boolean;
  onSelect?: () => void;
};

const AgentTypeIcon = {
  specialized: <IconAgent size="16" />,
  workflow: <IconCodeBranch size="16" />,
};

export function AgentCardView({ agent, isSelected = false, onSelect }: AgentCardViewProps) {
  const agentIcon = AgentTypeIcon[agent.agent_type] || <IconSparkles size="16" />;
  const nameRef = useRef<HTMLParagraphElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  useEffect(() => {
    const element = nameRef.current;
    if (element) {
      // Check if the text is truncated by comparing scroll height with client height
      setIsTruncated(element.scrollHeight > element.clientHeight);
    }
  }, [agent.name]);

  return (
    <Flex
      className={`${styles.cardViewContainer} ${isSelected ? styles.selected : ''}`}
      onClick={onSelect}
      style={{ cursor: onSelect ? 'pointer' : 'default' }}
    >
      {agent.is_installed &&
        (agent.installed_version !== null && agent.installed_version < agent.directory_version ? (
          <Tooltip content="New version available">
            <Box className={styles.statusIconWrapper}>
              <IconArrowsRotate className={styles.updateAvailable} />
            </Box>
          </Tooltip>
        ) : (
          <Tooltip content="Activated">
            <Box className={styles.statusIconWrapper}>
              <IconCheckCircleSolid className={styles.installed} />
            </Box>
          </Tooltip>
        ))}

      <Flex flexDirection="row" gap="8" alignItems="start">
        <Flex className={styles.icon}>{agentIcon}</Flex>
        <Flex flexDirection="row" justifyContent="space-between" alignItems="start" w="full">
          <Tooltip content={agent.name} disabled={!isTruncated}>
            <Text className={styles.agentName} fontSize="md" lineClamp="2" ref={nameRef}>
              {agent.name}
            </Text>
          </Tooltip>
        </Flex>
      </Flex>

      {/* Description */}
      <Text fontSize="sm" color="fg.tertiary" lineClamp={agent.tags?.length ? '2' : '3'}>
        {agent.description || 'No description available'}
      </Text>

      {/* Tags */}
      <TruncatedList lineClamp={1} gap="8">
        {agent.tags?.map(tag => (
          <Badge key={tag.name} intent="neutral">
            {tag.name}
          </Badge>
        ))}
      </TruncatedList>
    </Flex>
  );
}
